import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList, ReferenceLine } from "recharts";
import { ChevronDown, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CHART, ChartCard, ChartEmpty, Dot, SortTh, TooltipBox, axisTick, tableClass } from "./ui";
import { cn } from "@/lib/utils";

const SEVERITY = {
  Critical: { color: CHART.danger, text: "text-destructive", chip: "bg-destructive/15 text-destructive" },
  High: { color: "color-mix(in oklab, var(--destructive) 60%, var(--warning-500))", text: "text-destructive", chip: "bg-destructive/10 text-destructive" },
  Medium: { color: CHART.warning, text: "text-warning-700", chip: "bg-warning-500/15 text-warning-700" },
  Low: { color: CHART.success, text: "text-success-700", chip: "bg-success-500/15 text-success-700" },
};

const getSeverityLabel = (pct) => {
  if (pct >= 50) return "Critical";
  if (pct >= 30) return "High";
  if (pct >= 15) return "Medium";
  return "Low";
};

const sev = (pct) => SEVERITY[getSeverityLabel(pct)];

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0]?.payload || {};
  return (
    <TooltipBox title={d.stage}>
      <span>{d.from_count} &rarr; {d.to_count} sessions</span>
      <span className={cn("font-medium", sev(d.drop_off_pct).text)}>{d.drop_off_count} dropped ({d.drop_off_pct}%)</span>
      <span className="text-primary">Conversion: {d.conversion_pct}%</span>
      <span className="text-muted-foreground">Severity: {getSeverityLabel(d.drop_off_pct)}</span>
    </TooltipBox>
  );
};

const MiniStat = ({ value, label, className }: { value: string | number; label: string; className?: string }) => (
  <div className="abandonment-stat flex flex-col rounded-lg border border-border bg-background px-3 py-2">
    <span className={cn("abandonment-stat-value text-xl font-semibold tabular-nums", className)}>{value}</span>
    <span className="abandonment-stat-label text-xs text-muted-foreground">{label}</span>
  </div>
);

const AbandonmentChart = ({ analysis, funnel }: any) => {
  const [showTable, setShowTable] = useState(false);
  const card = (body) => (
    <ChartCard icon={<LogOut />} title="Drop-off Analysis" description="Where guests abandon the booking flow.">{body}</ChartCard>
  );

  if (!analysis || analysis.length === 0) {
    return card(<ChartEmpty icon={<LogOut />} text="No drop-off data for selected period" />);
  }

  const f = funnel || {};
  const totalDropped = analysis.reduce((sum, a) => sum + (a.drop_off_count || 0), 0);
  const worstStage = analysis.reduce(
    (worst, a) => (a.drop_off_pct > (worst?.drop_off_pct || 0) ? a : worst),
    analysis[0]
  );
  const avgDropPct = analysis.length > 0
    ? (analysis.reduce((s, a) => s + (a.drop_off_pct || 0), 0) / analysis.length).toFixed(1)
    : 0;
  const chartData = analysis.map((a) => ({
    ...a,
    conversion_pct: a.from_count > 0 ? ((a.to_count / a.from_count) * 100).toFixed(1) : "100.0",
  }));

  return card(
    <>
      <div className="abandonment-summary-row grid grid-cols-2 gap-2 md:grid-cols-4">
        <MiniStat value={totalDropped} label="Total Drop-offs" />
        <MiniStat value={`${avgDropPct}%`} label="Avg Drop-off Rate" />
        {worstStage && (
          <MiniStat value={`${worstStage.drop_off_pct}%`} className={sev(worstStage.drop_off_pct).text}
            label={`Worst: ${worstStage.stage?.split(" \u2192 ")[0] || "\u2014"}`} />
        )}
        {f.booking_sessions > 0 && f.booking_confirmed >= 0 && (
          <MiniStat value={`${((f.booking_confirmed / f.booking_sessions) * 100).toFixed(1)}%`} className="text-success-700" label="Overall Conversion" />
        )}
      </div>

      <div className="abandonment-legend flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><Dot color={SEVERITY.Low.color} />Low (&lt;15%)</span>
        <span className="inline-flex items-center gap-1.5"><Dot color={SEVERITY.Medium.color} />Medium (15-29%)</span>
        <span className="inline-flex items-center gap-1.5"><Dot color={SEVERITY.High.color} />High (30-49%)</span>
        <span className="inline-flex items-center gap-1.5"><Dot color={SEVERITY.Critical.color} />Critical (50%+)</span>
      </div>

      <ResponsiveContainer width="100%" height={Math.max(220, chartData.length * 40)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 90, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
          <XAxis type="number" tick={axisTick} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: CHART.text }} tickLine={false} axisLine={false} width={180} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "var(--accent)" }} />
          <ReferenceLine x={0} stroke={CHART.grid} />
          <Bar dataKey="drop_off_count" radius={[0, 4, 4, 0]} barSize={24}>
            {chartData.map((entry, idx) => <Cell key={idx} fill={sev(entry.drop_off_pct).color} />)}
            <LabelList dataKey="drop_off_pct" position="right" formatter={(v) => `${v}% drop`} style={{ fontSize: 11, fill: CHART.muted }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Button type="button" variant="ghost" size="sm" className="abandonment-toggle-btn self-start" onClick={() => setShowTable((prev) => !prev)}>
        <ChevronDown className={cn("transition-transform", showTable && "rotate-180")} aria-hidden="true" />
        {showTable ? "Hide Details" : "Show Details"}
      </Button>

      {showTable && (
        <div className="abandonment-detail-table-wrap overflow-x-auto rounded-lg border border-border">
          <table className={cn("abandonment-detail-table", tableClass)}>
            <thead className="bg-muted/50">
              <tr>
                {["Stage Transition", "Entered", "Continued", "Dropped", "Drop %", "Severity"].map((h) => (
                  <SortTh key={h} label={h} active={false} dir="desc" />
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, idx) => {
                const severity = getSeverityLabel(row.drop_off_pct);
                return (
                  <tr key={idx}>
                    <td>{row.stage}</td>
                    <td className="num-formatted tabular-nums">{row.from_count}</td>
                    <td className="num-formatted tabular-nums">{row.to_count}</td>
                    <td className={cn("num-formatted font-semibold tabular-nums", SEVERITY[severity].text)}>{row.drop_off_count}</td>
                    <td className={cn("num-formatted font-semibold tabular-nums", SEVERITY[severity].text)}>{row.drop_off_pct}%</td>
                    <td>
                      <span className={cn("abandonment-severity-badge rounded-full px-2 py-0.5 text-xs font-medium", SEVERITY[severity].chip)}>
                        {severity}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default AbandonmentChart;
